import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { Minus, Plus, MessageSquare, Thermometer, Palmtree, CalendarRange, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DayEntry, WageType, AbsenceType } from '../../types';
import { deriveEntryFlags } from '../../types';
import type { VacationRecalcEntry } from '../../utils/calculations';
import { validateHours, validateNote, parseGermanNumber, formatGermanNumber } from '../../utils/validation';
import { HelpIcon } from '../Help';
import { calculateAverageHoursForWeekday, round } from '../../utils/calculations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DayEditModalProps {
  date: Date;
  entry: DayEntry | null;
  entries: Record<string, DayEntry>;
  previousYearEntries?: Record<string, DayEntry>;
  onSave: (entry: DayEntry) => void;
  onClose: () => void;
  /** Alle Felder außer Manager-Note gesperrt (für Manager-Ansicht) */
  readOnly?: boolean;
  /** Manager-Note-Sektion mit editierbarem Textfeld anzeigen */
  showManagerNote?: boolean;
  /** Callback zum Speichern der Manager-Note (nur bei readOnly + showManagerNote) */
  onSaveManagerNote?: (note: string) => void;
  /** Callback um den VacationRangeDialog zu öffnen (mit diesem Datum als Start) */
  onOpenVacationRange?: (initialDate: string) => void;
  /** WageType des betrachteten Users — bestimmt ob Betriebsferien bearbeitbar sind. null/undefined = HOURLY (bearbeitbar). */
  viewedUserWageType?: WageType | null;
  /** Verbleibende Urlaubstage — für Info-Text unter Urlaubs-Checkboxen */
  remainingVacation?: number;
  /** Overlay mit dynamisch berechneten Urlaubsstunden für zukünftige Tage (§11 BUrlG) */
  vacationRecalcMap?: Map<string, VacationRecalcEntry>;
  /** Verfügbare Abwesenheitsarten (aus useAbsenceTypes Hook) */
  absenceTypes?: AbsenceType[];
}

export function DayEditModal({ date, entry, entries, previousYearEntries, onSave, onClose, readOnly = false, showManagerNote = false, onSaveManagerNote, onOpenVacationRange, viewedUserWageType, remainingVacation, vacationRecalcMap, absenceTypes = [] }: DayEditModalProps) {
  const dateString = format(date, 'yyyy-MM-dd');
  // Provisionale Urlaubsstunden aus dem Recalc-Overlay (§11 BUrlG)
  const recalcEntry = vacationRecalcMap?.get(dateString);
  const isProvisionalVacation = !!recalcEntry?.isProvisional;
  const hasVacationData = recalcEntry?.hasData ?? true;

  // Wenn ein Recalc-Wert vorliegt, diesen für die Stunden-Initialisierung verwenden
  const effectiveEntryHours = recalcEntry && entry?.isVacation
    ? recalcEntry.hours
    : entry?.hours;
  const effectiveVacationCalcHours = recalcEntry && (entry?.isVacation || entry?.isHalfVacation)
    ? recalcEntry.hours
    : entry?.vacationCalculatedHours;

  const [hours, setHours] = useState<string>(
    effectiveEntryHours ? formatGermanNumber(effectiveEntryHours, 1) : '0'
  );
  const [isHoliday, setIsHoliday] = useState(entry?.isHoliday ?? false);
  const [sickDayCalculatedHours, setSickDayCalculatedHours] = useState<number | undefined>(
    entry?.sickDayCalculatedHours
  );
  const [vacationCalculatedHours, setVacationCalculatedHours] = useState<number | undefined>(
    effectiveVacationCalcHours
  );

  // Abwesenheitsart-State: absenceTypeId + isHalfDay ersetzen die alten Checkbox-States
  const [selectedAbsenceTypeId, setSelectedAbsenceTypeId] = useState<string | null>(
    entry?.absenceTypeId ?? null
  );
  const [isHalfDay, setIsHalfDay] = useState(entry?.isHalfVacation ?? false);

  // Fallback: Wenn kein absenceTypeId gesetzt, aber alte Flags vorhanden → System-Typ ableiten
  const resolvedAbsenceTypeId = useMemo(() => {
    if (selectedAbsenceTypeId) return selectedAbsenceTypeId;
    if (!entry) return null;
    if (entry.isVacation || entry.isHalfVacation) {
      return absenceTypes.find((t) => t.systemKey === 'VACATION')?.id ?? null;
    }
    if (entry.isSickDay) {
      return absenceTypes.find((t) => t.systemKey === 'SICK_DAY')?.id ?? null;
    }
    return null;
  }, [selectedAbsenceTypeId, entry, absenceTypes]);

  // Abgeleitete Flags aus dem gewählten AbsenceType
  const selectedAbsenceType = useMemo(
    () => absenceTypes.find((t) => t.id === resolvedAbsenceTypeId) ?? null,
    [absenceTypes, resolvedAbsenceTypeId]
  );
  const derivedFlags = useMemo(
    () => deriveEntryFlags(selectedAbsenceType, isHalfDay),
    [selectedAbsenceType, isHalfDay]
  );
  const isVacation = derivedFlags.isVacation;
  const isHalfVacation = derivedFlags.isHalfVacation;
  const isSickDay = derivedFlags.isSickDay;
  const [note, setNote] = useState(entry?.note ?? '');
  const [errors, setErrors] = useState<{ hours?: string; note?: string }>({});

  // Urlaubsbuchungs-Block: Checkbox deaktivieren wenn kein Resturlaub mehr verfügbar
  const vacationExhausted = remainingVacation !== undefined && remainingVacation <= 0;

  // Betriebsferien-Logik: Nutzt den WageType des betrachteten Users.
  // HOURLY (oder null/undefined): Betriebsferien = bearbeitbar (Stunden ausblenden, Urlaub/Krankheit eintragbar).
  // FIXED: Betriebsferien = vollständig geblockt (Modal sollte gar nicht öffnen).
  const isHourlyCompanyVacation = !!(entry?.isCompanyVacation && viewedUserWageType !== 'FIXED');

  // Manager-Note State (nur für readOnly + showManagerNote)
  const [managerNoteText, setManagerNoteText] = useState(entry?.managerNote ?? '');
  const [isSavingManagerNote, setIsSavingManagerNote] = useState(false);

  // Berechne den durchschnittlichen Stundenwert für diesen Wochentag
  const averageHoursForWeekday = useMemo(() => {
    return calculateAverageHoursForWeekday(entries, date, previousYearEntries);
  }, [entries, date, previousYearEntries]);

  // Wochentag-Name für die Anzeige
  const weekdayName = format(date, 'EEEE', { locale: de });

  const hoursInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!readOnly) {
      hoursInputRef.current?.focus();
      hoursInputRef.current?.select();
    }
  }, [readOnly]);

  // Stunden sind gesperrt bei Krankheit, Urlaub, halbem Urlaub oder readOnly-Modus
  const hoursLocked = readOnly || isSickDay || isVacation || isHalfVacation;

  const handleHoursChange = (value: string) => {
    if (hoursLocked) return; // Stunden sind bei Krankheit/Urlaub gesperrt
    // Erlaube nur Zahlen, Komma und Punkt
    const cleaned = value.replace(/[^0-9.,]/g, '');
    setHours(cleaned);

    const numValue = parseGermanNumber(cleaned);
    const error = validateHours(numValue);
    setErrors((prev) => ({ ...prev, hours: error?.message }));
  };

  const adjustHours = (delta: number) => {
    if (hoursLocked) return; // Stunden sind bei Krankheit/Urlaub gesperrt
    const currentHours = parseGermanNumber(hours);
    const newHours = Math.max(0, Math.min(24, currentHours + delta));
    setHours(formatGermanNumber(newHours, 1));
    setErrors((prev) => ({ ...prev, hours: undefined }));
  };

  /**
   * Findet den ersten Tag einer zusammenhängenden Krankheitsphase.
   * Wochenenden werden übersprungen (unterbrechen die Phase nicht).
   */
  const findFirstSickDayOfPhase = (): { date: Date; calculatedHours: number | undefined } => {
    let checkDate = subDays(date, 1);
    let firstSickDay = date;
    let calculatedHours: number | undefined = undefined;

    // Rückwärts suchen nach zusammenhängenden Krankheitstagen
    let searching = true;
    while (searching) {
      const checkDateStr = format(checkDate, 'yyyy-MM-dd');
      const checkEntry = entries[checkDateStr];
      const dayOfWeek = checkDate.getDay();

      // Wochenenden und Feiertage überspringen (unterbrechen die Phase nicht)
      if (dayOfWeek === 0 || dayOfWeek === 6 || checkEntry?.isHoliday) {
        checkDate = subDays(checkDate, 1);
        continue;
      }

      // Prüfe ob der Tag krank ist
      if (checkEntry?.isSickDay) {
        firstSickDay = checkDate;
        // Übernehme den gespeicherten Wert, falls vorhanden
        if (checkEntry.sickDayCalculatedHours !== undefined) {
          calculatedHours = checkEntry.sickDayCalculatedHours;
        }
        checkDate = subDays(checkDate, 1);
      } else {
        // Krankheitsphase endet hier
        searching = false;
      }
    }

    return { date: firstSickDay, calculatedHours };
  };

  /**
   * Setzt Stundenfeld und berechneten Wert gemeinsam auf denselben Betrag.
   *
   * Zwei Nachkommastellen sind hier keine Kosmetik: mit einer Stelle wurde ein
   * §11-Wert von 5,25 als "5,3" ins Stundenfeld geschrieben und als 5,30
   * gespeichert, während der berechnete Wert 5,25 blieb. Der Lohn-Export
   * summiert `hours`, die Meldung wich also vom Rechtswert ab.
   */
  const applyCalculatedHours = (value: number, kind: 'vacation' | 'sick') => {
    setHours(formatGermanNumber(value, 2));
    if (kind === 'vacation') {
      setVacationCalculatedHours(value);
      setSickDayCalculatedHours(undefined);
    } else {
      setSickDayCalculatedHours(value);
      setVacationCalculatedHours(undefined);
    }
  };

  /**
   * §11-Basiswert eines ganzen Urlaubstags: der Wert der laufenden
   * Urlaubsphase, sonst der 13-Wochen-Durchschnitt dieses Wochentags.
   * Die Phasensuche folgt nur ganzen Urlaubstagen, der Wert ist also nie
   * bereits halbiert.
   */
  const fullDayVacationHours = (): number => {
    const { calculatedHours } = findFirstVacationDayOfPhase();
    return calculatedHours ?? averageHoursForWeekday;
  };

  /**
   * Zentraler Handler für Abwesenheitsart-Wechsel.
   * Ersetzt die alten handleVacationChange/handleHalfVacationChange/handleSickDayChange.
   */
  const handleAbsenceTypeChange = (typeId: string) => {
    if (typeId === '_none') {
      // "Keine Abwesenheit" gewählt → alles zurücksetzen
      setSelectedAbsenceTypeId(null);
      setIsHalfDay(false);
      setVacationCalculatedHours(undefined);
      setSickDayCalculatedHours(undefined);
      setHours('0');
      return;
    }

    const type = absenceTypes.find((t) => t.id === typeId);
    if (!type) return;

    setSelectedAbsenceTypeId(typeId);
    setIsHalfDay(false); // Halber Tag wird separat gesetzt

    if (type.countsAsSickDay) {
      // Krankheits-Logik: EFZG-Berechnung
      const { calculatedHours: existingCalculatedHours } = findFirstSickDayOfPhase();
      applyCalculatedHours(existingCalculatedHours ?? averageHoursForWeekday, 'sick');
    } else if (type.countsAsVacation) {
      // Urlaubs-Logik: §11 BUrlG
      applyCalculatedHours(fullDayVacationHours(), 'vacation');
    } else {
      // Sonstiger Typ: Stunden auf 0
      setVacationCalculatedHours(undefined);
      setSickDayCalculatedHours(undefined);
      setHours('0');
    }
    setErrors((prev) => ({ ...prev, hours: undefined }));
  };

  /** Handler für "Halber Tag"-Checkbox (nur bei urlaubswirksamen Typen) */
  const handleHalfDayChange = (checked: boolean) => {
    setIsHalfDay(checked);
    if (selectedAbsenceType?.countsAsVacation) {
      // Halber Urlaubstag ist die Hälfte des §11-Werts, nicht die Hälfte der
      // Vollzeit-Sollzeit — bei Teilzeit- und Stundenkräften liegt
      // hoursPerDayFullTime / 2 systematisch über dem Urlaubsentgelt.
      const fullDay = fullDayVacationHours();
      applyCalculatedHours(checked ? round(fullDay / 2, 2) : fullDay, 'vacation');
    }
    setErrors((prev) => ({ ...prev, hours: undefined }));
  };

  /**
   * Findet den ersten Tag einer zusammenhängenden Urlaubsphase.
   * Wochenenden werden übersprungen (unterbrechen die Phase nicht).
   */
  const findFirstVacationDayOfPhase = (): { date: Date; calculatedHours: number | undefined } => {
    let checkDate = subDays(date, 1);
    let firstVacationDay = date;
    let calculatedHours: number | undefined = undefined;

    // Rückwärts suchen nach zusammenhängenden Urlaubstagen
    let searching = true;
    while (searching) {
      const checkDateStr = format(checkDate, 'yyyy-MM-dd');
      const checkEntry = entries[checkDateStr];
      const dayOfWeek = checkDate.getDay();

      // Wochenenden und Feiertage überspringen (unterbrechen die Phase nicht)
      if (dayOfWeek === 0 || dayOfWeek === 6 || checkEntry?.isHoliday) {
        checkDate = subDays(checkDate, 1);
        continue;
      }

      // Prüfe ob der Tag Urlaub ist
      if (checkEntry?.isVacation) {
        firstVacationDay = checkDate;
        // Übernehme den gespeicherten Wert, falls vorhanden
        if (checkEntry.vacationCalculatedHours !== undefined) {
          calculatedHours = checkEntry.vacationCalculatedHours;
        }
        checkDate = subDays(checkDate, 1);
      } else {
        // Urlaubsphase endet hier
        searching = false;
      }
    }

    return { date: firstVacationDay, calculatedHours };
  };


  const handleNoteChange = (value: string) => {
    setNote(value);
    const error = validateNote(value);
    setErrors((prev) => ({ ...prev, note: error?.message }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const numHours = parseGermanNumber(hours);
    const hoursError = validateHours(numHours);
    const noteError = validateNote(note);

    if (hoursError || noteError) {
      setErrors({
        hours: hoursError?.message,
        note: noteError?.message,
      });
      return;
    }

    onSave({
      date: dateString,
      hours: isHourlyCompanyVacation && !isSickDay && !isVacation && !isHalfVacation ? 0 : numHours,
      isHoliday,
      isVacation,
      isHalfVacation,
      isSickDay,
      isCompanyVacation: entry?.isCompanyVacation,
      absenceTypeId: resolvedAbsenceTypeId,
      // Berechnete Stunden für Krankheitstage (nach EFZG)
      sickDayCalculatedHours: isSickDay ? sickDayCalculatedHours : undefined,
      // Berechnete Stunden für Urlaubstage (nach §11 BUrlG).
      // Halbe Urlaubstage setzen isHalfVacation statt isVacation — ohne sie
      // wird der berechnete Wert verworfen und der Tag gilt als nicht
      // finalisiert, obwohl Stunden gespeichert sind.
      vacationCalculatedHours: isVacation || isHalfVacation ? vacationCalculatedHours : undefined,
      note: note.trim() || undefined,
      // Manager-Anmerkung beibehalten
      managerNote: entry?.managerNote,
      managerNoteAuthorId: entry?.managerNoteAuthorId,
      managerNoteAuthorName: entry?.managerNoteAuthorName,
      managerNoteTimestamp: entry?.managerNoteTimestamp,
    });
  };

  const handleSaveManagerNote = async () => {
    if (!onSaveManagerNote) return;
    setIsSavingManagerNote(true);
    try {
      await onSaveManagerNote(managerNoteText.trim());
    } finally {
      setIsSavingManagerNote(false);
    }
  };

  const formattedDate = format(date, 'EEEE, d. MMMM yyyy', { locale: de });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-md"
        onOpenAutoFocus={(e) => {
          // Prevent Radix default focus behavior so our useEffect can focus the hours input
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{formattedDate}</DialogTitle>
        </DialogHeader>

        <form onSubmit={readOnly ? (e) => e.preventDefault() : handleSubmit} className="space-y-5">
          {/* Betriebsferien-Hinweis für HOURLY-Mitarbeiter */}
          {isHourlyCompanyVacation && (
            <div className="border border-indigo-300/60 bg-indigo-50 dark:border-indigo-500/20 dark:bg-indigo-900/10 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-indigo-900 dark:text-indigo-100 mb-1">
                    Betriebsferien
                  </h4>
                  <p className="text-xs text-indigo-700 dark:text-indigo-300">
                    Keine Arbeitszeiterfassung möglich. Urlaub oder Krankheit können eingetragen werden.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Arbeitsstunden (ausgeblendet bei HOURLY-Betriebsferien) */}
          {!isHourlyCompanyVacation && <div>
            <Label htmlFor="hours" className="label flex items-center gap-1">
              Arbeitsstunden
              {!readOnly && <HelpIcon section="kalender" />}
              {hoursLocked && (
                <span className={cn('ml-2 text-xs font-normal', isSickDay ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400')}>
                  (automatisch berechnet)
                </span>
              )}
            </Label>
            <div className="flex items-center gap-2">
              {!readOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => adjustHours(-0.5)}
                  aria-label="Stunden verringern"
                  disabled={hoursLocked}
                >
                  <Minus className="w-4 h-4" />
                </Button>
              )}
              <Input
                ref={hoursInputRef}
                type="text"
                id="hours"
                inputMode="decimal"
                value={hours}
                onChange={(e) => handleHoursChange(e.target.value)}
                disabled={hoursLocked}
                readOnly={readOnly}
                className={cn(
                  'text-center text-input-value font-medium',
                  errors.hours && 'border-red-500 focus:ring-red-500',
                  hoursLocked && 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                )}
                aria-describedby={errors.hours ? 'hours-error' : 'hours-help'}
              />
              {!readOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => adjustHours(0.5)}
                  aria-label="Stunden erhöhen"
                  disabled={hoursLocked}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              )}
            </div>
            {!readOnly && errors.hours ? (
              <p id="hours-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors.hours}
              </p>
            ) : !readOnly ? (
              <p id="hours-help" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {hoursLocked ? 'Stunden werden automatisch aus dem 13-Wochen-Durchschnitt berechnet' : '0 bis 24 Stunden (Dezimalzahlen erlaubt)'}
              </p>
            ) : null}
          </div>}

          {/* Feiertag-Checkbox (ausgeblendet bei Betriebsferien) */}
          {!isHourlyCompanyVacation && (
            <Label className={cn('flex items-center gap-3', readOnly ? 'cursor-default' : 'cursor-pointer')}>
              <Checkbox
                checked={isHoliday}
                onCheckedChange={(checked) => setIsHoliday(checked as boolean)}
                disabled={readOnly}
              />
              <span className="text-gray-700 dark:text-gray-200">Feiertag</span>
            </Label>
          )}

          {/* Abwesenheitsart-Dropdown */}
          <div className="space-y-3">
            <div>
              <Label className="label">Abwesenheitsart</Label>
              <Select
                value={resolvedAbsenceTypeId ?? '_none'}
                onValueChange={handleAbsenceTypeChange}
                disabled={readOnly}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Keine Abwesenheit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Keine Abwesenheit</SelectItem>
                  {absenceTypes
                    .filter((t) => t.systemKey !== 'HOLIDAY' && t.systemKey !== 'COMPANY_VACATION')
                    .map((type) => {
                      // Urlaubswirksame Typen deaktivieren wenn kein Resturlaub
                      const disabled = type.countsAsVacation && vacationExhausted && resolvedAbsenceTypeId !== type.id;
                      return (
                        <SelectItem key={type.id} value={type.id} disabled={disabled}>
                          <span className="flex items-center gap-2">
                            {type.icon && <span>{type.icon}</span>}
                            {type.name}
                          </span>
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
              {/* Resturlaub-Info */}
              {selectedAbsenceType?.countsAsVacation && remainingVacation !== undefined && (
                <p className={cn(
                  'text-xs mt-1',
                  vacationExhausted ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
                )}>
                  {vacationExhausted
                    ? 'Kein Resturlaub mehr verfügbar. Wenden Sie sich an die Personalabteilung.'
                    : `Zählt als ${isHalfDay ? '0,5' : '1'} Tag · ${remainingVacation} verbleibend`}
                </p>
              )}
            </div>

            {/* Halber Tag Checkbox (nur bei Typen mit allowHalfDay + countsAsVacation) */}
            {selectedAbsenceType && selectedAbsenceType.allowHalfDay && selectedAbsenceType.countsAsVacation && (
              <Label className={cn(
                'flex items-center gap-3',
                readOnly || (vacationExhausted && !isHalfDay) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
              )}>
                <Checkbox
                  checked={isHalfDay}
                  onCheckedChange={(checked) => handleHalfDayChange(checked as boolean)}
                  disabled={readOnly || (vacationExhausted && !isHalfDay)}
                />
                <span className="text-gray-700 dark:text-gray-200">Halber Tag (0,5)</span>
              </Label>
            )}
          </div>

          {/* Urlaubsblock-Button */}
          {!readOnly && onOpenVacationRange && date.getDay() !== 0 && date.getDay() !== 6 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full flex items-center gap-2 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={() => {
                onOpenVacationRange(dateString);
                onClose();
              }}
            >
              <CalendarRange className="w-4 h-4" />
              Urlaub für mehrere Tage…
            </Button>
          )}

          {/* Krankheit Info-Box */}
          {isSickDay && (
            <div className="border border-amber-300/60 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-900/10 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Thermometer className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
                    Entgeltfortzahlung (EFZG)
                  </h4>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {sickDayCalculatedHours !== undefined && sickDayCalculatedHours !== averageHoursForWeekday ? (
                      <>
                        Wert aus bestehender Krankheitsphase übernommen:{' '}
                        <strong>{formatGermanNumber(sickDayCalculatedHours, 2)} Std.</strong>
                        <br />
                        <span className="text-xs opacity-75">
                          (Aktueller {weekdayName}-Durchschnitt wäre: {formatGermanNumber(averageHoursForWeekday, 2)} Std.)
                        </span>
                      </>
                    ) : sickDayCalculatedHours !== undefined && sickDayCalculatedHours > 0 ? (
                      <>
                        13-Wochen-Durchschnitt für {weekdayName}:{' '}
                        <strong>{formatGermanNumber(sickDayCalculatedHours, 2)} Std.</strong>
                      </>
                    ) : (
                      <>
                        Keine Arbeitsdaten für {weekdayName} in den letzten 13 Wochen vorhanden.
                        Es werden 0 Stunden eingetragen.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Urlaub Info-Box */}
          {isVacation && (
            <div className="border border-blue-300/60 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-900/10 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Palmtree className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                    Urlaubsentgelt (§11 BUrlG) <HelpIcon section="vorlaeufig" />
                  </h4>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    {isProvisionalVacation && !hasVacationData ? (
                      <>
                        Noch keine Arbeitsdaten im Berechnungszeitraum vorhanden.
                        <br />
                        <span className="text-xs opacity-75 italic">
                          Urlaubsstunden werden berechnet, sobald Arbeitsdaten vor dem Urlaubsbeginn vorliegen.
                        </span>
                      </>
                    ) : isProvisionalVacation ? (
                      <>
                        13-Wochen-Durchschnitt für {weekdayName}:{' '}
                        <strong>{formatGermanNumber(vacationCalculatedHours ?? 0, 2)} Std.</strong>
                        {' '}
                        <span className="italic opacity-75">(vorläufig)</span>
                        <br />
                        <span className="text-xs opacity-75">
                          Wird bei Urlaubsantritt endgültig berechnet.
                        </span>
                      </>
                    ) : vacationCalculatedHours !== undefined && vacationCalculatedHours !== averageHoursForWeekday ? (
                      <>
                        Wert aus bestehender Urlaubsphase übernommen:{' '}
                        <strong>{formatGermanNumber(vacationCalculatedHours, 2)} Std.</strong>
                        <br />
                        <span className="text-xs opacity-75">
                          (Aktueller {weekdayName}-Durchschnitt wäre: {formatGermanNumber(averageHoursForWeekday, 2)} Std.)
                        </span>
                      </>
                    ) : vacationCalculatedHours !== undefined && vacationCalculatedHours > 0 ? (
                      <>
                        13-Wochen-Durchschnitt für {weekdayName}:{' '}
                        <strong>{formatGermanNumber(vacationCalculatedHours, 2)} Std.</strong>
                      </>
                    ) : (
                      <>
                        Keine Arbeitsdaten für {weekdayName} in den letzten 13 Wochen vorhanden.
                        Es werden 0 Stunden eingetragen.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Manager-Anmerkung (readonly für Mitarbeiter, editierbar für Manager) */}
          {showManagerNote ? (
            <div className="space-y-2">
              <Label htmlFor="managerNote" className="label flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                Manager-Anmerkung
              </Label>
              <Textarea
                id="managerNote"
                value={managerNoteText}
                onChange={(e) => setManagerNoteText(e.target.value)}
                rows={3}
                maxLength={200}
                className="resize-none"
                placeholder="Anmerkung für den Mitarbeiter..."
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {managerNoteText.length} / 200
                </p>
                {entry?.managerNoteAuthorName && entry?.managerNote && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Zuletzt: {entry.managerNoteAuthorName}
                    {entry.managerNoteTimestamp && (
                      <> • {new Date(entry.managerNoteTimestamp).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</>
                    )}
                  </p>
                )}
              </div>
            </div>
          ) : entry?.managerNote ? (
            <div className="border border-blue-300/60 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-900/10 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                    Manager-Anmerkung
                  </h4>
                  <p className="text-xs text-blue-700 dark:text-blue-300 whitespace-pre-wrap break-words">
                    {entry.managerNote}
                  </p>
                  {entry.managerNoteAuthorName && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                      Von: {entry.managerNoteAuthorName}
                      {entry.managerNoteTimestamp && (
                        <> • {new Date(entry.managerNoteTimestamp).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Bemerkung des Mitarbeiters */}
          {readOnly ? (
            entry?.note ? (
              <div>
                <p className="label">Anmerkung des Mitarbeiters</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                  {entry.note}
                </p>
              </div>
            ) : null
          ) : (
            <div>
              <Label htmlFor="note" className="label">
                Bemerkung (optional)
              </Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => handleNoteChange(e.target.value)}
                rows={2}
                maxLength={200}
                className={cn(
                  'resize-none',
                  errors.note && 'border-red-500 focus:ring-red-500'
                )}
                aria-describedby={errors.note ? 'note-error' : undefined}
              />
              {errors.note && (
                <p id="note-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.note}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 text-right">
                {note.length} / 200
              </p>
            </div>
          )}

          {/* Buttons */}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {readOnly ? 'Schließen' : 'Abbrechen'}
            </Button>
            {readOnly && showManagerNote && onSaveManagerNote ? (
              <Button
                type="button"
                onClick={handleSaveManagerNote}
                disabled={isSavingManagerNote}
                className="flex items-center gap-2"
              >
                {isSavingManagerNote && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                Anmerkung speichern
              </Button>
            ) : !readOnly ? (
              <Button type="submit">
                Speichern
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
